"use client";

import { LoadingOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./index.module.css";

interface VerificationCodeInputProps {
  email: string;
  isVerifying: boolean;
  hasError?: boolean;
  onVerify: (code: string) => void;
  onResend: () => Promise<void>;
  onClearCode?: () => void;
  resendCooldown?: number; // in seconds, default 60
  className?: string;
}

export function VerificationCodeInput({
  email,
  isVerifying,
  hasError = false,
  onVerify,
  onResend,
  onClearCode,
  resendCooldown = 60,
  className,
}: VerificationCodeInputProps) {
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [countdown, setCountdown] = useState(resendCooldown);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isInitialized = useRef(false);
  const hasSubmitted = useRef(false);

  // Initialize countdown and focus on mount
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      setCountdown(resendCooldown);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [resendCooldown]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // Clear code when hasError becomes true
  useEffect(() => {
    if (hasError && onClearCode) {
      setCode(["", "", "", "", "", ""]);
      hasSubmitted.current = false;
      inputRefs.current[0]?.focus();
      onClearCode();
    }
  }, [hasError, onClearCode]);

  // Auto-submit when all 6 digits are filled
  useEffect(() => {
    const fullCode = code.join("");
    if (fullCode.length === 6 && !isVerifying && !hasSubmitted.current) {
      hasSubmitted.current = true;
      onVerify(fullCode);
    }
  }, [code, isVerifying, onVerify]);

  const handleInputChange = useCallback(
    (index: number, value: string) => {
      // Only allow digits
      const digit = value.replace(/\D/g, "").slice(-1);

      const newCode = [...code];
      newCode[index] = digit;
      setCode(newCode);
      hasSubmitted.current = false;

      // Auto-focus next input
      if (digit && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [code]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        if (!code[index] && index > 0) {
          // If current input is empty, focus previous input
          inputRefs.current[index - 1]?.focus();
        } else {
          // Clear current input
          const newCode = [...code];
          newCode[index] = "";
          setCode(newCode);
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === "ArrowRight" && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [code]
  );

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newCode = pastedData.split("").concat(Array(6).fill("")).slice(0, 6);
    setCode(newCode);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex((digit) => !digit);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  }, []);

  const handleResend = useCallback(async () => {
    if (countdown > 0 || isResending) return;

    setIsResending(true);
    try {
      await onResend();
      setCountdown(resendCooldown);
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsResending(false);
    }
  }, [countdown, isResending, onResend, resendCooldown]);

  return (
    <div className={classNames(styles.container, className)}>
      <p className={styles.description}>
        We&apos;ve sent a verification code to{" "}
        <span className={styles.email}>{email}</span>. Please check your inbox.
      </p>

      <div className={styles.codeInputContainer}>
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className={classNames(styles.codeInput, {
              [styles.filled]: digit !== "",
              [styles.error]: hasError,
            })}
            disabled={isVerifying}
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </div>

      {isVerifying && (
        <div className={styles.verifyingStatus}>
          <LoadingOutlined className={styles.spinner} />
          <span>Verifying...</span>
        </div>
      )}

      {!isVerifying && (
        <div className={styles.resendContainer}>
          {countdown > 0 ? (
            <span className={styles.countdown}>Resend in {countdown}s</span>
          ) : (
            <button
              className={styles.resendButton}
              onClick={handleResend}
              disabled={isResending}
            >
              {`Didn't receive your code?`}
              <span className={styles.resendLink}>
                {isResending ? "Sending..." : "Resend"}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
